<template>
    <b-modal :id="modalId" size="xl" title="Electronic Prior Authorization Details" @cancel="cancelClicked"
        @hidden="hidden" @ok="okClicked" @show="show">
        <template #modal-footer="{ ok, cancel, close }">
            <b-col>
                <b-button @click="close()">Close</b-button>
            </b-col>
            <b-col class="text-right">
                <b-button class="mx-2" variant="warning" v-if="!needsAnswers"
                    @click="acknowledgeEpa()" :disabled="!!lockOwnerData">Acknowledge</b-button>
                <b-button class="mx-2" variant="danger" @click="cancel()" :disabled="!!lockOwnerData">Cancel</b-button>
                <b-button class="mx-2" variant="success" @click="ok()" :disabled="!!lockOwnerData">Ok</b-button>
            </b-col>
        </template>
        <b-container>
            <b-col style="text-align:right">

                <router-link :to="{ name: 'PatientDashboard', params: { id: patientId } }" target="_blank">
                    <span>Go To Profile</span> <i class="fas fa-user"></i>
                </router-link>
                <span> | </span>

                <router-link :to="prescriptionDetailsRoute" target="_blank">
                    <span>Go To Rx</span> <i class="fas fa-file-prescription"></i>
                </router-link>
            </b-col>
            <div v-if="isComplete && epaDetails">
                <b-row v-if="epaDetails && epaDetails.id">
                    <b-col cols="3"><label class="font-weight-bold">Id:</label></b-col>
                    <b-col> {{ epaDetails.id }}</b-col>
                </b-row>
                <b-row v-if="rxId">
                    <b-col cols="3"><label class="font-weight-bold">Rx Id:</label></b-col>
                    <b-col> {{ rxId }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.referenceId">
                    <b-col cols="3"><label class="font-weight-bold">Reference Id:</label></b-col>
                    <b-col> {{ epaDetails.referenceId }}</b-col>
                </b-row>
                <b-row v-if="epaDetails">
                    <b-col cols="3"><label class="font-weight-bold">Priority:</label></b-col>
                    <b-col>
                        {{ epaDetails.priorityIndicator == 0 ? 'Standard' : 'Expedited' }}
                    </b-col>
                </b-row>
                <b-row v-if="epaDetails">
                    <b-col cols="3"><label class="font-weight-bold">Status:</label></b-col>
                    <b-col> {{ formatStatus(epaDetails.status) }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.caseId">
                    <b-col cols="3"><label class="font-weight-bold">Case Id:</label></b-col>
                    <b-col> {{ epaDetails.caseId }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.effective">
                    <b-col cols="3"><label class="font-weight-bold">Effective:</label></b-col>
                    <b-col>{{ epaDetails.effective | formatLocalDate('L') }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.expiration">
                    <b-col cols="3"><label class="font-weight-bold">Expiration:</label></b-col>
                    <b-col>{{ epaDetails.expiration | formatLocalDate('L') }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.deadline">
                    <b-col cols="3"><label class="font-weight-bold">Deadline:</label></b-col>
                    <b-col>{{ epaDetails.deadline | formatLocalDate('L') }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.expectedResponse">
                    <b-col cols="3"><label class="font-weight-bold">ExpectedResponse:</label></b-col>
                    <b-col>{{ epaDetails.expectedResponse | formatLocalDate('L') }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.note && !isCancelled">
                    <b-col cols="3"><label class="font-weight-bold">Note:</label></b-col>
                    <b-col>{{ epaDetails.note }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.authorizationNumber">
                    <b-col cols="3"><label class="font-weight-bold">Authorization Number:</label></b-col>
                    <b-col>{{ epaDetails.authorizationNumber }}</b-col>
                </b-row>
                <b-row v-if="epaDetails && epaDetails.appealNote">
                    <b-col cols="3"><label class="font-weight-bold">Appeal Note:</label></b-col>
                    <b-col>{{ epaDetails.appealNote }}</b-col>
                </b-row>

                <b-row style="display: none">
                    <b-col>
                        {{ epaDetails }}
                    </b-col>
                </b-row>
            </div>
            <b-row v-if="epaDetails && epaDetails.questionSet">
                <b-col cols="3"><label class="font-weight-bold">Attachment Required?</label></b-col>
                <b-col>{{ epaDetails.questionSet.requiresAttachment ? 'YES' : 'NO' }}</b-col>
            </b-row>
            <b-row v-if="epaDetails && epaDetails.questionSet && epaDetails.questionSet.attachmentNotes">
                <b-col cols="3"><label class="font-weight-bold">Attachment Notes:</label></b-col>
                <b-col>{{ epaDetails.questionSet.attachmentNotes }}</b-col>
            </b-row>

            <b-row>
                <b-col cols="3"><label class="font-weight-bold">Appeal Supported:</label></b-col>
                <b-col>{{ isAppeal ? 'YES' : 'NO' }}</b-col>
            </b-row>
            <div v-if="isAppeal">

                <b-row>
                    <b-col>
                        <b>Appeal Note: </b>{{ epaDetails && epaDetails.note }}
                    </b-col>
                </b-row>
                <b-row>
                    <b-form-input v-model.trim="appealText" placeholder="Appeal Feedback"></b-form-input>
                </b-row>

            </div>

            <div v-if="patient && shouldShowDocumentsForm">
                <PatientDocumentsForm ref="patient-documents-form" listOnly :patient="patient">
                    <template #cell(view)="row">
                        <b-checkbox v-model="selectedDocument" :unchecked-value="null" :value="row.data.item.id">
                            <a v-b-tooltip href="" title="View" @click.prevent="openDocument(row.data.item)">
                                <i class="now-ui-icons ui-1_zoom-bold"></i>
                            </a>
                        </b-checkbox>

                    </template>
                </PatientDocumentsForm>
            </div>

            <b-row v-if="epaDetails && epaDetails.notes && epaDetails.notes.length > 0">
                <b-col>
                    <b-table id="epa-notes-table" :fields="[{ key: 'body', label: 'Notes' }]" :items="epaDetails.notes">
                    </b-table>
                </b-col>
            </b-row>

            <div v-if="needsAnswers">
                <b-row>
                    <b-col>
                        <div>
                            {{ epaDetails.questionSet.title }}
                            <br />
                            {{ epaDetails.questionSet.description }}
                            <br />
                            Contact Phone: {{ epaDetails.questionSet.contactPhone }}
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button variant="danger" @click="resetAllChoices">Reset</b-button>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-table id="questionsTable" :busy="isLoadingDetails" :fields="questionFields" :filter="filter"
                            :filter-function="filterFunc" :items="questions" empty-text="No Additional Information"
                            show-empty sort-by="sequenceNumber">
                            <template #table-busy>
                                <div class="text-center text-danger my-2">
                                    <b-spinner class="align-middle"></b-spinner>
                                    <strong>Loading...</strong>
                                </div>
                            </template>
                            <template #cell(clear)="data">
                                <b-button size="sm" variant="link" class="p-1" @click="clearAnswer(data.item.answer)">
                                    <i class="fa fa-close text-danger" v-b-tooltip.hover title="Clear Answer"/>
                                </b-button>
                            </template>
                            <template #cell(choices)="data">
                                <div v-if="data.item.choices.length > 1 && !shouldShowNumericInput(data.item)">
                                    <div v-if="canChooseMultiple(data.item)">
                                        <b-form-checkbox-group v-model="multiSelected"
                                            :options="choiceMapping(data.item.choices)"
                                            stacked></b-form-checkbox-group>
                                    </div>
                                    <div v-else>
                                        <b-form-radio-group v-model="data.item.answer.choiceId"
                                            :options="choiceMapping(data.item.choices)"
                                            stacked></b-form-radio-group>
                                    </div>
                                </div>
                                <div v-if="shouldShowDateInput(data.item)">
                                    <fg-input>
                                        <el-date-picker v-model="data.item.answer.text" format="MM/dd/yyyy" type="date">
                                        </el-date-picker>
                                    </fg-input>
                                </div>
                                <div v-if="shouldShowNumericInput(data.item)">
                                    <b-form-input v-model="data.item.answer.choiceId" type="number"></b-form-input>
                                </div>
                                <div v-if="shouldShowTextInput(data.item)">
                                    <b-form-input v-model.trim="data.item.answer.text"></b-form-input>
                                </div>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>
            </div>
            <div>
                <NotesForm v-if="patient" :entityId="epaId" :entityType="noteEntityType" @saved="refreshNotesList" />
                <NotesList ref="epaNotesList" :entityId="epaId" :entityType="noteEntityType" :pageSize="5" />
            </div>
        </b-container>
        <b-modal id="epa-cancel-modal" ref="epa-cancel-modal"
            title="Are you sure you want to cancel the Prior Authorization?" @ok="cancelEpa">
            <b-select v-model="selectedCancelReason" :options="cancelCodes"></b-select>
        </b-modal>
    </b-modal>
</template>

<script lang="ts">
import { Component, Emit, Mixins, Prop, Ref, VModel, Watch } from "vue-property-decorator";
import Axios, { AxiosError, AxiosRequestConfig } from "axios";
import { LockHandler } from "@/mixins/LockHandler";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { BModal, BvModalEvent } from "bootstrap-vue";
import {
    AllowsAdditionalText, ElectronicPriorAuthorization, ElectronicPriorAuthStatus, EpaAnswer, EpaChoice, EpaQuestion,
} from "@/models/ElectronicPriorAuthorization";
import PatientDocumentsForm from "@/components/Patient/PatientDocumentsForm.vue";
import { Patient } from "@/models/Patient";
import { Prescription } from "@/models/Prescription";
import { DatePicker } from "element-ui";
import NotificationTypes = NotificationOptions.NotificationTypes;
import EnumSelector from "@/components/EnumSelector.vue";
import TextUtils from "@/util/TextUtils";
import { PatientDocument } from "@/models/PatientDocument";
import axios from "axios";
import NotesList from "../Note/NotesList.vue";
import NotesForm from '@/components/Note/NotesForm.vue';
import { EntityType } from "@/models/EntityType";

@Component({
    components: {
        EnumSelector,
        PatientDocumentsForm,
        [DatePicker.name]: DatePicker,
        NotesList,
        NotesForm,
    }
})
export default class ElectronicPriorAuthorizationModal extends Mixins(LockHandler) {
    @Prop({ default: "electronic-prior-authorization-details-modal" }) public modalId!: string;
    @VModel() private electronicPriorAuthorization?: ElectronicPriorAuthorization;
    @Ref('epa-cancel-modal') private epaCancelModal!: BModal;
    private patient: Patient | null = null;
    private epaDetails: ElectronicPriorAuthorization | null = null;
    private selectedCancelReason: string | null = null;
    private selectedDocument: number | null = null;
    private questions? = [] as EpaQuestion[];
    private lockOwnerData: any = null;
    protected multiSelected = [];
    private appealText = "";
    protected isLoadingDetails = false;
    protected questionFields = [
        { key: "id" },
        { key: "text" },
        { key: "choices", },
        // {key: "answer"},
        {key: "clear"},
        // {key: "show_details"}
    ];
    protected noteEntityType = EntityType.ElectronicPriorAuthorization;

    protected cancelCodes = [
        { value: null, text: 'Select an option' },
        { value: "BY", text: "Other" },
        { value: "AC", text: "Patient no longer under Provider care" },
        { value: "BW", text: "Change to a different medication" }
    ]

    get epaId() {
        return this.electronicPriorAuthorization?.id;
    }

    get isAppeal() {
        const canAppeal = this.electronicPriorAuthorization?.status == ElectronicPriorAuthStatus.RejectedCanAppeal
        return canAppeal || this.electronicPriorAuthorization?.appealSupported;
    }

    get isComplete() {
        const isApproved = this.electronicPriorAuthorization?.status == ElectronicPriorAuthStatus.Approved;
        const isDenied = this.electronicPriorAuthorization?.status == ElectronicPriorAuthStatus.Denied;
        const isCanceled = this.electronicPriorAuthorization?.status == ElectronicPriorAuthStatus.Canceled
            || this.electronicPriorAuthorization?.status == ElectronicPriorAuthStatus.CancelApproved
            || this.electronicPriorAuthorization?.status == ElectronicPriorAuthStatus.CancelRejected;
        return isApproved || isDenied || isCanceled;
    }

    get isCancelled() {
        return this.epaDetails?.status == ElectronicPriorAuthStatus.CancelApproved
            || this.epaDetails?.status == ElectronicPriorAuthStatus.CancelRejected;
    }

    get needsAnswers() {
        const needAnswers = this.electronicPriorAuthorization?.status == ElectronicPriorAuthStatus.QuestionsNeedAnswers;
        const submittedAnswers = this.electronicPriorAuthorization?.status == ElectronicPriorAuthStatus.AnswersSubmitted;
        return this.epaDetails && this.epaDetails.questionSet && (needAnswers || submittedAnswers);
    }

    get authorizationNumber() {
        return this.epaDetails?.authorizationNumber;
    }

    get epaNote() {
        return this.epaDetails?.note;
    }

    get rxId() {
        return `${this.epaDetails?.storeId}-${this.epaDetails?.rxNumber}-${this.epaDetails?.rfNumber}`;
    }

    get shouldShowDocumentsForm() {
        return true;
    }

    get filter() {
        const nextQuestionIds = this.questions?.filter(q => q.answer.text || q.answer.choiceId).flatMap(q => q.nextQuestionId) ?? [];
        const choiceNextQuestionIds = this.questions?.flatMap(q =>
            q.choices.filter(c => {
                if (c.id == q.answer.choiceId) return true;
                if (!c.nextQuestionId) return false;
                if (!c.lowerBoundComparisonValue) return c.id.toString()?.startsWith("NumericType");

                if (q.answer.choiceId == null) return false;
                const nAnswer = Number(q.answer.choiceId);
                // console.log("nAnswer: ", nAnswer);
                let lowerComparisonPass = true;
                if (c.lowerBoundComparisonOperator == "GE") {
                    lowerComparisonPass = nAnswer >= c.lowerBoundComparisonValue;
                } else if (c.lowerBoundComparisonOperator == "GT") {
                    lowerComparisonPass = nAnswer > c.lowerBoundComparisonValue;
                } else if (c.lowerBoundComparisonOperator == "EQ") {
                    lowerComparisonPass = nAnswer == c.lowerBoundComparisonValue;
                } else if (c.lowerBoundComparisonOperator == "NE") {
                    lowerComparisonPass = nAnswer != c.lowerBoundComparisonValue;
                } else if (c.lowerBoundComparisonOperator == "LE") {
                    lowerComparisonPass = nAnswer <= c.lowerBoundComparisonValue;
                } else if (c.lowerBoundComparisonOperator == "LT") {
                    lowerComparisonPass = nAnswer < c.lowerBoundComparisonValue;
                }

                if (!c.upperBoundComparisonValue || !lowerComparisonPass) return lowerComparisonPass;
                let upperComparisonPass = true;
                if (c.upperBoundComparisonOperator == "LE") {
                    upperComparisonPass = nAnswer <= c.upperBoundComparisonValue;
                } else if (c.upperBoundComparisonOperator == "LT") {
                    upperComparisonPass = nAnswer < c.upperBoundComparisonValue;
                } else if (c.upperBoundComparisonOperator == "GE") {
                    upperComparisonPass = nAnswer >= c.upperBoundComparisonValue;
                } else if (c.upperBoundComparisonOperator == "GT") {
                    upperComparisonPass = nAnswer > c.upperBoundComparisonValue;
                } else if (c.upperBoundComparisonOperator == "EQ") {
                    upperComparisonPass = nAnswer == c.upperBoundComparisonValue;
                } else if (c.upperBoundComparisonOperator == "NE") {
                    upperComparisonPass = nAnswer != c.upperBoundComparisonValue;
                }
                return upperComparisonPass || lowerComparisonPass;
            })
                .map(c => c.nextQuestionId)
        ) ?? [];

        let questionIdsToShow = nextQuestionIds.concat(choiceNextQuestionIds).filter(s => s != null);
        const initialQuestion = this.questions?.find(q => q.sequenceNumber == 0 || q.sequenceNumber == 1)?.id as string;
        if (initialQuestion) questionIdsToShow.push(initialQuestion);

        return questionIdsToShow;
    }

    get prescriptionDetailsRoute() {
        return {
            name: 'PrescriptionDetails'
            , params: {
                storeID: this.epaDetails?.storeId,
                rxNumber: this.epaDetails?.rxNumber,
                rfNumber: (this.epaDetails?.rfNumber || 0)
            }
        };
    }

    get patientId() {
        if (this.patient == null) {
            this.loadPatient();
            return 0;
        }
        return this.patient.id;
    }

    choiceMapping(choices:EpaChoice[]) {
        return choices.map(c => { return { text: c.text, value: c.id } });
    }

    formatStatus(status: ElectronicPriorAuthStatus) {
        return TextUtils.camelCaseToNormal(ElectronicPriorAuthStatus[status]);
    }

    resetAllChoices() {
        const answers = this.questions?.flatMap(q => q.answer) ?? [];
        for (const answer of answers) {
            answer.text = null;
            answer.choiceId = null;
        }
    }

    clearAnswer(item: EpaAnswer) {
        item.text = null;
        item.choiceId = null;
    }

    filterFunc(item: EpaQuestion, filter: string[]) {
        return filter.includes(item.id as string);
    }

    @Watch('multiSelected')
    choiceUpdate(item: string[]) {
        if (!item) return;
        console.log("multiSelected: ", item)
        if (!this.questions) return;
        this.questions.forEach(question => {
            let wasReset = false;
            question.choices.forEach(c => {
                if (question && question.answer) {
                    if (item.includes(c.id as string)) {
                        if (!wasReset) {
                            question.answer.choiceId = "";
                            wasReset = true;
                        }
                        if (question.answer?.choiceId != null) {
                            if (question.answer.choiceId.length > 0) question.answer.choiceId += ", ";
                            question.answer.choiceId += c.id;
                        }
                    }
                }
            })
        });
    }

    canChooseMultiple(item: EpaQuestion) {
        return item.selectMultiple == true;
    }

    @Watch("shouldShowDocumentsForm")
    loadDocuments() {
        if (!this.electronicPriorAuthorization?.storeId) return;
        if (this.shouldShowDocumentsForm && this.patient == null) {
            this.loadPatient();
        }
    }

    loadPatient() {
        if (this.electronicPriorAuthorization?.storeId == undefined) return;
        this.isLoadingDetails = true;
        const rxId = `${this.electronicPriorAuthorization?.storeId}-${this.electronicPriorAuthorization?.rxNumber}-${this.electronicPriorAuthorization?.rfNumber}`;
        Axios.get<Prescription>(`/Prescription/${rxId}`)
            .then(resp => {
                let rx = new Prescription(resp.data);
                Axios.get<Patient>(`/Patient/${rx.patientID}`)
                    .then(ptResp => {
                        this.patient = new Patient(ptResp.data);
                    })
                    .catch(err => {
                        this.$notification(NotificationOptions.error(err));
                    })
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            })
            .finally(() => {
                this.isLoadingDetails = false;
            });
    }

    reset() {
        this.patient = null;
        this.epaDetails = null;
        this.selectedCancelReason = null;
        this.selectedDocument = null;
        this.questions = [];
        this.multiSelected = [];
        this.appealText = "";
        this.isLoadingDetails = false;
    }

    @Emit()
    async hidden() {
        // Await the release before @Emit fires 'hidden': the host refreshes its EPA table on
        // 'hidden', and re-fetching before the release POST lands re-paints a stale "locked"
        // padlock for an EPA that is actually free. @Emit waits for this promise to resolve.
        await this.clearAllLocks();
        this.reset();
    }

    @Emit()
    show() {
        console.log("showing.isAppeal?", this.isAppeal)
        this.loadDocuments();
        this.acquireEpaLock();
    }

    @Watch("epaId")
    onEpaIdChange(newId: number | undefined) {
        if (!newId) return;
        this.acquireEpaLock();
    }

    async acquireEpaLock() {
        const id = this.epaId;
        if (!id) {
            await this.clearAllLocks();
            return;
        }

        try {
            await this.setExclusiveLock(`/PriorAuthorization/Electronic/${id}/lock`, 60000);
            this.lockOwnerData = null;
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.lockOwnerData = error.response.data;
                this.showLockedMessage(
                    EntityType.ElectronicPriorAuthorization,
                    id.toString(),
                    id.toString(),
                    error.response.data,
                );
            } else {
                this.$notification(NotificationOptions.error(err));
            }
        }
    }

    @Watch('selectedDocument')
    selectedDocumentUpdate(val: any, _oldVal: any) {
        console.log("Selected Document change: ", val);
    }

    @Watch('electronicPriorAuthorization.id')
    epaChange(newValue: string) {
        console.log("new value for epa.id", newValue);
        if (!newValue) return;

        this.isLoadingDetails = true;
        Axios.get<ElectronicPriorAuthorization>(`/PriorAuthorization/Electronic/${newValue}`)
            .then(response => {
                console.log("epaDetails", response.data);
                this.epaDetails = response.data;
                this.questions = this.epaDetails?.questionSet?.questions ?? null;
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            })
            .finally(() => this.isLoadingDetails = false);
    }

    choiceTableId(data: any) {
        const id = `choiceTable${data.item.id}`;
        console.log("choiceTableId: ", id);
        return id;
    }

    shouldShowDateInput(item: EpaQuestion) {
        return item.choices.some(c => c.id == "DateType");
    }

    shouldShowTextInput(item: EpaQuestion) {
        if (this.shouldShowDateInput(item)) return false;
        return item.choices.some(c => c.allowsAdditionalText == AllowsAdditionalText.Required
            || c.allowsAdditionalText == AllowsAdditionalText.Optional);
    }

    shouldShowNumericInput(item: EpaQuestion) {
        return item.choices.some(c => c.id.toString()?.startsWith("NumericType"));
    }

    acknowledgeEpa() {
        Axios.post(`PriorAuthorization/Electronic/${this.epaDetails?.id}/Acknowledge`)
            .then(() => {
                this.$bvModal.hide(this.modalId);
            })
            .catch(err => {
                if (this.handleLockedResponse(err)) return;
                this.$notification(NotificationOptions.error(err));
            });
    }

    async cancelEpa(event: BvModalEvent) {
        if (!event) return;
        event.preventDefault();

        const epaId = this.epaDetails?.id;
        const config: AxiosRequestConfig = { params: { reason: this.selectedCancelReason } };

        try {
            const epaCancelResp = await Axios.post(`PriorAuthorization/Electronic/${epaId}/Cancel`, null, config);
            console.log("epaCancelResp: ", epaCancelResp);
            this.$notification(NotificationOptions.notificationOptionsPreset("Electronic Prior Authorization Cancelled.", NotificationTypes.success));
            if (event.componentId) this.$bvModal.hide(event.componentId);
            this.patient = null;
            this.epaDetails = null;
        } catch (err) {
            // Close the cancel-reason submodal before surfacing the locked-by overlay so
            // BV's modal stack doesn't drop the msgBoxConfirm during the submodal's hide animation.
            if (event.componentId) this.$bvModal.hide(event.componentId);
            if (this.handleLockedResponse(err)) return;
            this.$notification(NotificationOptions.error(err));
            this.patient = null;
            this.epaDetails = null;
        }
    }

    cancelClicked(event: BvModalEvent) {
        // Another user holds the lock — surface the locked-by overlay instead of closing
        // the EPA modal or letting the user start a cancel-reason flow that will 418.
        if (this.lockOwnerData) {
            event.preventDefault();
            const id = this.epaDetails?.id ?? this.epaId;
            if (id) {
                this.showLockedMessage(
                    EntityType.ElectronicPriorAuthorization,
                    id.toString(),
                    id.toString(),
                    this.lockOwnerData,
                );
            }
            return;
        }

        if (!this.epaDetails?.id) return;
        if (!this.questions || this.questions.length == 0) return;
        if (!event.componentId) {
            this.$notification(NotificationOptions.error("Unknown error cancelling Electronic Prior Authorization"));
            return;
        }

        event.preventDefault();

        this.epaCancelModal.show();
    }

    okClicked(event: BvModalEvent) {
        if (!this.epaDetails?.id) return;
        if (!this.questions || this.questions.length == 0) return;
        if (!event.componentId) {
            this.$notification(NotificationOptions.error("Unknown error saving Electronic Prior Authorization"));
            return;
        }

        if (this.selectedDocument) this.epaDetails.attachmentId = this.selectedDocument;

        let method: ((event: BvModalEvent) => Promise<void>) | undefined = undefined;

        if (this.isAppeal) method = this.finishAppeal;
        if (this.needsAnswers) method = this.finishAnswers;

        if (method) {
            event.preventDefault();
            console.log("attempting normal event");
            method(event)
                .catch(err => {
                    this.$notification(NotificationOptions.error(err));
                })
                .finally(() => {
                    this.patient = null;
                    this.epaDetails = null;
                });
        }
    }

    async finishAppeal(event: BvModalEvent) {
        try {
            if (this.epaDetails) {
                this.epaDetails.appealNote = this.appealText;
            }
            const epaAppealResp = await Axios.post(`PriorAuthorization/Electronic/${this.epaDetails?.id}/Appeal`, this.epaDetails);
            console.log("epaAppealResp: ", epaAppealResp);
            if (event.componentId) this.$bvModal.hide(event.componentId);
        } catch (err) {
            if (this.handleLockedResponse(err)) return;
            this.$notification(NotificationOptions.error(err));
        }
    }

    async finishAnswers(event: BvModalEvent) {
        try {
            const epaAnswerResp = await Axios.post(`PriorAuthorization/Electronic/${this.epaDetails?.id}/Answer`, this.epaDetails);
            console.log("epaAnswerResp: ", epaAnswerResp);
            if (event.componentId) this.$bvModal.hide(event.componentId);
        } catch (err) {
            if (this.handleLockedResponse(err)) return;
            this.$notification(NotificationOptions.error(err));
        }
    }

    handleLockedResponse(err: unknown): boolean {
        const error = err as AxiosError;
        if (error.response?.status !== 418) return false;

        const id = this.epaDetails?.id ?? this.epaId;
        if (!id) return false;

        this.showLockedMessage(
            EntityType.ElectronicPriorAuthorization,
            id.toString(),
            id.toString(),
            error.response.data,
        );
        return true;
    }

    async openDocument(pd: PatientDocument) {
        try {
            const res = await axios.get(`image/${pd.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    refreshNotesList() {
        if (this.$refs.epaNotesList instanceof NotesList)
            (this.$refs.patientNotesList as any).loadNotes();
    }
}
</script>

<style scoped></style>
